import { computed, ref, watch } from "vue";
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { mensajeError, formatFecha } from "./reputacionHelpers.js";

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Rangos rápidos: devuelven [desde, hasta]
const RANGOS = {
    mes: (h) => [toISO(new Date(h.getFullYear(), h.getMonth(), 1)), toISO(h)],
    mesAnterior: (h) => [
        toISO(new Date(h.getFullYear(), h.getMonth() - 1, 1)),
        toISO(new Date(h.getFullYear(), h.getMonth(), 0)),
    ],
    '30dias': (h) => { const d = new Date(h); d.setDate(d.getDate() - 29); return [toISO(d), toISO(h)]; },
    '90dias': (h) => { const d = new Date(h); d.setDate(d.getDate() - 89); return [toISO(d), toISO(h)]; },
    anio: (h) => [toISO(new Date(h.getFullYear(), 0, 1)), toISO(h)],
    todo: () => [null, null],
};

const RANGO_DEFAULT = 'mes';

// Filtros que viajan en la URL (para compartir o recargar el reporte)
const CAMPOS_ID = ['agent_id', 'ciudad_id', 'tipo_inmueble', 'tipo_negocio', 'inmobiliaria_id'];

const filtrosDeRango = (rango) => {
    const [fecha_desde, fecha_hasta] = RANGOS[rango](new Date());
    return {
        fecha_desde, fecha_hasta,
        agent_id: null, ciudad_id: null, tipo_inmueble: null, tipo_negocio: null, inmobiliaria_id: null,
    };
};

const diasEntre = (desde, hasta) => (new Date(hasta) - new Date(desde)) / 86400000 + 1;

// Agrupación que da una gráfica legible para el rango elegido
const agrupacionSugerida = (desde, hasta) => {
    if (!desde || !hasta) return 'mes';
    const dias = diasEntre(desde, hasta);
    if (dias <= 31) return 'dia';
    if (dias <= 180) return 'semana';
    return 'mes';
};

export function useReputacionInmuebles() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const state = store.state.appReputacionInmuebles;

    const alert = ref({ state: false, message: '', color: 'success' });
    const sinAcceso = ref(false);
    const iniciado = ref(false);
    const loadingFiltros = ref(false);
    const loadingResumen = ref(false);
    const loadingTendencia = ref(false);
    const loadingTabla = ref(false);
    const loadingDetalle = ref(false);
    const exportando = ref(false);

    const filtros = ref(filtrosDeRango(RANGO_DEFAULT));
    const rangoActivo = ref(RANGO_DEFAULT);
    const agrupacion = ref(agrupacionSugerida(filtros.value.fecha_desde, filtros.value.fecha_hasta));
    const tab = ref('inmuebles');

    const tablaInmuebles = ref({
        estado: 'calificados', orden: 'mejor', q: '', min_calificaciones: '',
        promedio_min: '', promedio_max: '', page: 1, por_pagina: 20,
    });
    const tablaAgentes = ref({ estado: 'calificados', orden: 'mejor', min_calificaciones: '', page: 1, por_pagina: 20 });

    const detalleAbierto = ref(false);
    const detalleId = ref(null);

    let alertTimer = null;
    const showAlert = (message, color = 'success') => {
        alert.value = { state: true, message, color };
        if (alertTimer) clearTimeout(alertTimer);
        alertTimer = setTimeout(() => {
            alert.value.state = false;
        }, 6000);
    };

    // Un 403 en cualquier endpoint = el usuario no es admin: se oculta el reporte
    const manejarError = async (error, porDefecto) => {
        if (error?.response?.status === 403) {
            sinAcceso.value = true;
            return;
        }
        showAlert(await mensajeError(error, porDefecto), 'danger');
    };

    // --- Estado derivado -------------------------------------------------
    const opciones = computed(() => state.opciones);
    const resumen = computed(() => state.resumen);
    const tendencia = computed(() => state.tendencia);
    const inmuebles = computed(() => state.inmuebles);
    const agentes = computed(() => state.agentes);
    const detalle = computed(() => state.detalle);

    const esPlataforma = computed(() => !!opciones.value.alcance?.plataforma);
    // Admin de plataforma viendo todas las inmobiliarias: la tabla muestra la columna Inmobiliaria
    const vistaGlobal = computed(() => esPlataforma.value && !filtros.value.inmobiliaria_id);

    // Primera carga (esqueletos) vs. refresco (se atenúa lo que ya se ve)
    const cargandoPrimeraVez = computed(() => !resumen.value && (loadingResumen.value || !iniciado.value));
    const refrescando = computed(() => !!resumen.value && loadingResumen.value);
    const sinCalificaciones = computed(() => !!resumen.value && !Number(resumen.value.kpis?.total_calificaciones));

    const toOptions = (lista, label = (x) => x.nombre) =>
        (lista || []).map((x) => ({ value: x.id, label: label(x) }));

    const opcionesInmobiliarias = computed(() =>
        toOptions(opciones.value.inmobiliarias, (x) => (x.email ? `${x.nombre} — ${x.email}` : x.nombre))
    );
    const opcionesAgentes = computed(() =>
        toOptions(opciones.value.agentes, (x) => `${x.nombre}${x.activo === false ? ' (inactivo)' : ''}`)
    );
    const opcionesCiudades = computed(() => toOptions(opciones.value.ciudades));
    const opcionesTiposInmueble = computed(() => toOptions(opciones.value.tipos_inmueble));
    const opcionesTiposNegocio = computed(() => toOptions(opciones.value.tipos_negocio));

    const nombreDe = (lista, id) => (lista.find((o) => o.value === id) || {}).label || `#${id}`;

    // Chips de los filtros secundarios activos (se quitan con la ×)
    const filtrosActivos = computed(() => {
        const f = filtros.value;
        const chips = [];
        if (f.inmobiliaria_id) chips.push({ campo: 'inmobiliaria_id', label: 'Inmobiliaria', valor: nombreDe(opcionesInmobiliarias.value, f.inmobiliaria_id) });
        if (f.agent_id) chips.push({ campo: 'agent_id', label: 'Agente', valor: nombreDe(opcionesAgentes.value, f.agent_id) });
        if (f.ciudad_id) chips.push({ campo: 'ciudad_id', label: 'Ciudad', valor: nombreDe(opcionesCiudades.value, f.ciudad_id) });
        if (f.tipo_inmueble) chips.push({ campo: 'tipo_inmueble', label: 'Tipo', valor: nombreDe(opcionesTiposInmueble.value, f.tipo_inmueble) });
        if (f.tipo_negocio) chips.push({ campo: 'tipo_negocio', label: 'Negocio', valor: nombreDe(opcionesTiposNegocio.value, f.tipo_negocio) });
        return chips;
    });

    const hayFiltrosModificados = computed(() =>
        filtrosActivos.value.length > 0 || rangoActivo.value !== RANGO_DEFAULT
    );

    // La API rechaza más de 366 días / 260 semanas / 120 meses
    const agrupacionesDisponibles = computed(() => {
        const { fecha_desde, fecha_hasta } = filtros.value;
        if (!fecha_desde || !fecha_hasta) return { dia: true, semana: true, mes: true };
        const dias = diasEntre(fecha_desde, fecha_hasta);
        return { dia: dias <= 366, semana: dias / 7 <= 260, mes: dias / 30.44 <= 120 };
    });

    const periodoTexto = computed(() => {
        const { fecha_desde, fecha_hasta } = filtros.value;
        if (!fecha_desde && !fecha_hasta) return 'Todo el histórico de calificaciones';
        if (!fecha_desde) return `Hasta el ${formatFecha(fecha_hasta)}`;
        if (!fecha_hasta) return `Desde el ${formatFecha(fecha_desde)}`;
        return `Del ${formatFecha(fecha_desde)} al ${formatFecha(fecha_hasta)}`;
    });

    // --- URL -------------------------------------------------------------
    const leerQuery = () => {
        const q = route.query;
        if (q.rango && RANGOS[q.rango]) {
            filtros.value = filtrosDeRango(q.rango);
            rangoActivo.value = q.rango;
        } else if (q.desde || q.hasta) {
            filtros.value.fecha_desde = q.desde || null;
            filtros.value.fecha_hasta = q.hasta || null;
            rangoActivo.value = 'custom';
        }
        CAMPOS_ID.forEach((campo) => {
            if (q[campo]) filtros.value[campo] = Number(q[campo]);
        });
        if (q.tab === 'agentes') tab.value = 'agentes';
        agrupacion.value = agrupacionSugerida(filtros.value.fecha_desde, filtros.value.fecha_hasta);
    };

    const escribirQuery = () => {
        const query = {};
        if (rangoActivo.value === 'custom') {
            if (filtros.value.fecha_desde) query.desde = filtros.value.fecha_desde;
            if (filtros.value.fecha_hasta) query.hasta = filtros.value.fecha_hasta;
        } else if (rangoActivo.value !== RANGO_DEFAULT) {
            query.rango = rangoActivo.value;
        }
        CAMPOS_ID.forEach((campo) => {
            if (filtros.value[campo]) query[campo] = String(filtros.value[campo]);
        });
        if (tab.value === 'agentes') query.tab = 'agentes';
        router.replace({ query }).catch(() => null);
    };

    // --- Carga -----------------------------------------------------------
    const getFiltros = async () => {
        loadingFiltros.value = true;
        try {
            const params = filtros.value.inmobiliaria_id ? { inmobiliaria_id: filtros.value.inmobiliaria_id } : {};
            await store.dispatch('appReputacionInmuebles/getFiltrosAction', params);
            return true;
        } catch (error) {
            await manejarError(error, 'Error al cargar los filtros del reporte.');
            return false;
        } finally {
            loadingFiltros.value = false;
        }
    };

    const getResumen = async () => {
        loadingResumen.value = true;
        try {
            await store.dispatch('appReputacionInmuebles/getResumenAction', filtros.value);
        } catch (error) {
            await manejarError(error, 'Error al cargar el resumen de reputación.');
        } finally {
            loadingResumen.value = false;
        }
    };

    const getTendencia = async () => {
        loadingTendencia.value = true;
        try {
            await store.dispatch('appReputacionInmuebles/getTendenciaAction', {
                ...filtros.value,
                agrupacion: agrupacion.value,
            });
        } catch (error) {
            await manejarError(error, 'Error al cargar la tendencia.');
        } finally {
            loadingTendencia.value = false;
        }
    };

    const getInmuebles = async () => {
        loadingTabla.value = true;
        try {
            await store.dispatch('appReputacionInmuebles/getInmueblesAction', {
                ...filtros.value,
                ...tablaInmuebles.value,
                q: tablaInmuebles.value.q.trim(),
            });
        } catch (error) {
            await manejarError(error, 'Error al cargar el ranking de inmuebles.');
        } finally {
            loadingTabla.value = false;
        }
    };

    const getAgentes = async () => {
        loadingTabla.value = true;
        try {
            await store.dispatch('appReputacionInmuebles/getAgentesAction', {
                ...filtros.value,
                ...tablaAgentes.value,
            });
        } catch (error) {
            await manejarError(error, 'Error al cargar el ranking de agentes.');
        } finally {
            loadingTabla.value = false;
        }
    };

    const getTabla = () => (tab.value === 'agentes' ? getAgentes() : getInmuebles());

    const cargarReporte = async () => {
        await Promise.all([getResumen(), getTendencia(), getTabla()]);
    };

    const iniciar = async () => {
        leerQuery();
        const ok = await getFiltros();
        if (ok && !sinAcceso.value) await cargarReporte();
        iniciado.value = true;
    };

    // --- Acciones de filtros ---------------------------------------------
    const validarFechas = () => {
        const { fecha_desde, fecha_hasta } = filtros.value;
        if (fecha_desde && fecha_hasta && fecha_desde > fecha_hasta) {
            showAlert('La fecha inicial no puede ser mayor que la final.', 'warning');
            return false;
        }
        return true;
    };

    const aplicarFiltros = async () => {
        if (!validarFechas()) return;
        agrupacion.value = agrupacionSugerida(filtros.value.fecha_desde, filtros.value.fecha_hasta);
        tablaInmuebles.value.page = 1;
        tablaAgentes.value.page = 1;
        escribirQuery();
        await cargarReporte();
    };

    const aplicarRango = async (rango) => {
        const [desde, hasta] = RANGOS[rango](new Date());
        filtros.value.fecha_desde = desde;
        filtros.value.fecha_hasta = hasta;
        rangoActivo.value = rango;
        await aplicarFiltros();
    };

    // Fechas escritas a mano: se aplica al salir del campo
    const cambiarFecha = async () => {
        rangoActivo.value = 'custom';
        await aplicarFiltros();
    };

    // Los agentes dependen de la inmobiliaria: se recargan las opciones y se
    // descarta el agente elegido (si no, la API responde 422)
    const cambiarInmobiliaria = async (id) => {
        filtros.value.inmobiliaria_id = id || null;
        filtros.value.agent_id = null;
        await getFiltros();
        await aplicarFiltros();
    };

    const cambiarFiltro = async (campo, valor) => {
        if (campo === 'inmobiliaria_id') return cambiarInmobiliaria(valor);
        filtros.value[campo] = valor || null;
        await aplicarFiltros();
    };

    const quitarFiltro = (campo) => cambiarFiltro(campo, null);

    const limpiarFiltros = async () => {
        const habiaInmobiliaria = !!filtros.value.inmobiliaria_id;
        filtros.value = filtrosDeRango(RANGO_DEFAULT);
        rangoActivo.value = RANGO_DEFAULT;
        if (habiaInmobiliaria) await getFiltros();
        await aplicarFiltros();
    };

    const cambiarAgrupacion = async (valor) => {
        if (!agrupacionesDisponibles.value[valor] || agrupacion.value === valor) return;
        agrupacion.value = valor;
        await getTendencia();
    };

    // --- Tabla -----------------------------------------------------------
    const cambiarTab = async (valor) => {
        if (tab.value === valor) return;
        tab.value = valor;
        escribirQuery();
        await getTabla();
    };

    const buscarTabla = async () => {
        if (tab.value === 'agentes') tablaAgentes.value.page = 1;
        else tablaInmuebles.value.page = 1;
        await getTabla();
    };

    // Búsqueda de texto con debounce, sin tener que pulsar Enter
    let busquedaTimer = null;
    watch(() => tablaInmuebles.value.q, () => {
        if (busquedaTimer) clearTimeout(busquedaTimer);
        busquedaTimer = setTimeout(buscarTabla, 400);
    });

    const cambiarPagina = async (accion) => {
        const tabla = tab.value === 'agentes' ? tablaAgentes : tablaInmuebles;
        const paginacion = (tab.value === 'agentes' ? agentes : inmuebles).value.paginacion;
        let pagina = tabla.value.page;
        if (accion === 'next') pagina = Math.min(pagina + 1, paginacion.ultima_pagina);
        else if (accion === 'previous') pagina = Math.max(pagina - 1, 1);
        else pagina = Number(accion);
        if (pagina === tabla.value.page) return;
        tabla.value.page = pagina;
        await getTabla();
    };

    // Desde el ranking de agentes: ver los inmuebles de ese agente
    const verInmueblesDeAgente = async (agente) => {
        if (!agente?.id) return;
        tab.value = 'inmuebles';
        await cambiarFiltro('agent_id', agente.id);
    };

    // --- Detalle ---------------------------------------------------------
    const abrirDetalle = async (inmuebleId) => {
        detalleId.value = inmuebleId;
        detalleAbierto.value = true;
        loadingDetalle.value = true;
        try {
            await store.dispatch('appReputacionInmuebles/getDetalleAction', {
                id: inmuebleId,
                params: {
                    fecha_desde: filtros.value.fecha_desde,
                    fecha_hasta: filtros.value.fecha_hasta,
                    inmobiliaria_id: filtros.value.inmobiliaria_id,
                },
            });
        } catch (error) {
            detalleAbierto.value = false;
            await manejarError(error, 'Error al cargar el detalle del inmueble.');
        } finally {
            loadingDetalle.value = false;
        }
    };

    const cerrarDetalle = () => {
        detalleAbierto.value = false;
        detalleId.value = null;
    };

    // --- Exportar --------------------------------------------------------
    const exportar = async () => {
        if (!validarFechas()) return;
        exportando.value = true;
        try {
            const response = await store.dispatch('appReputacionInmuebles/exportarAction', {
                ...filtros.value,
                ...tablaInmuebles.value,
                q: tablaInmuebles.value.q.trim(),
                page: undefined,
                por_pagina: undefined,
            });

            // Si la API responde JSON en lugar del CSV, es un error
            if ((response.headers['content-type'] || '').includes('application/json')) {
                throw { response: { status: response.status, data: response.data } };
            }

            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const nombre = match ? match[1] : `reputacion-inmuebles-${toISO(new Date())}.csv`;

            const url = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = nombre;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            showAlert('Reporte exportado.', 'success');
        } catch (error) {
            await manejarError(error, 'Error al exportar el reporte.');
        } finally {
            exportando.value = false;
        }
    };

    return {
        alert, sinAcceso, showAlert,
        loadingFiltros, loadingResumen, loadingTendencia, loadingTabla, loadingDetalle, exportando,
        cargandoPrimeraVez, refrescando, sinCalificaciones,
        filtros, rangoActivo, agrupacion, agrupacionesDisponibles, tab, tablaInmuebles, tablaAgentes,
        detalleAbierto, detalleId,
        opciones, resumen, tendencia, inmuebles, agentes, detalle,
        esPlataforma, vistaGlobal, periodoTexto,
        opcionesInmobiliarias, opcionesAgentes, opcionesCiudades, opcionesTiposInmueble, opcionesTiposNegocio,
        filtrosActivos, hayFiltrosModificados,
        iniciar, cargarReporte, getTendencia, getTabla,
        aplicarRango, cambiarFecha, cambiarFiltro, quitarFiltro, limpiarFiltros, cambiarAgrupacion,
        cambiarTab, buscarTabla, cambiarPagina, verInmueblesDeAgente,
        abrirDetalle, cerrarDetalle, exportar,
    };
}
